/**
 * Registry sidecar component for navigating registries and document types.
 * Resizable sidebar with search and tree view of registries.
 */

import { FileText, PanelLeftClose, Search, X } from "lucide-react";
import { type MouseEvent as ReactMouseEvent, useCallback, useMemo, useRef, useState } from "react";
import { RegistryTree } from "@/components/RegistryTree";
import { useDocumentTypeCounts } from "@/hooks/useDocumentTypeCounts";
import { useInvoiceCountsByRegistry, useTotalInvoiceCount } from "@/hooks/useInvoices";
import { useRegistries } from "@/hooks/useRegistries";
import { useRegistryTree } from "@/hooks/useRegistryTree";
import type { TabType } from "@/lib/url-state";

interface RegistrySidecarProps {
  /** Current width in pixels */
  width: number;
  /** Callback when width changes during resize */
  onWidthChange: (width: number) => void;
  /** Minimum allowed width */
  minWidth: number;
  /** Maximum allowed width */
  maxWidth: number;
  /** Callback to close/collapse the sidecar */
  onClose: () => void;
  /** Currently selected registry ID (null for "All Registries") */
  selectedRegistryId: string | null;
  /** Callback when a registry is selected */
  onRegistrySelect: (registryId: string | null) => void;
  /** Currently selected document type */
  selectedDocumentType?: TabType;
  /** Callback when a document type is selected */
  onDocumentTypeSelect?: (type: TabType) => void;
}

/**
 * Renders a resizable sidecar for registry navigation.
 * Includes search, tree view, and resize handle.
 *
 * @example
 * <RegistrySidecar
 *   width={240}
 *   onWidthChange={setWidth}
 *   minWidth={180}
 *   maxWidth={320}
 *   onClose={toggleSidecar}
 *   selectedRegistryId={selectedId}
 *   onRegistrySelect={handleSelect}
 * />
 */
export function RegistrySidecar({
  width,
  onWidthChange,
  minWidth,
  maxWidth,
  onClose,
  selectedRegistryId,
  onRegistrySelect,
  selectedDocumentType,
  onDocumentTypeSelect,
}: RegistrySidecarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch registries and invoice counts
  const { data: registries, isLoading: isRegistriesLoading, error: registriesError } = useRegistries();
  const registryIds = useMemo(() => registries?.map((r) => r.registryId) ?? [], [registries]);
  const { counts: invoiceCounts, isLoading: isCountsLoading } = useInvoiceCountsByRegistry(registryIds);
  const { data: totalCount, isLoading: isTotalLoading } = useTotalInvoiceCount();

  // Fetch document type counts for badges
  const { counts: documentTypeCounts } = useDocumentTypeCounts();

  // Filter registries based on search query
  const filteredRegistries = useMemo(() => {
    if (!registries) {
      return undefined;
    }
    if (!searchQuery.trim()) {
      return registries;
    }
    const query = searchQuery.toLowerCase();
    return registries.filter(
      (registry) => registry.name.toLowerCase().includes(query) || registry.sequenceValue.toLowerCase().includes(query),
    );
  }, [registries, searchQuery]);

  // Group filtered registries by year
  const groups = useRegistryTree(filteredRegistries, invoiceCounts);

  // Handle resize drag (ported from ChatSidebar)
  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = width;

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        // Resize from right edge (opposite to ChatSidebar which resizes from left)
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
        onWidthChange(newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [width, minWidth, maxWidth, onWidthChange],
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, []);

  return (
    <aside
      className="relative flex h-full flex-col border-r bg-white dark:border-gray-700 dark:bg-gray-900"
      style={{ width }}
      data-testid="registry-sidecar"
    >
      {/* Resize handle on right edge */}
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-ew-resize hover:bg-blue-500"
        onMouseDown={handleResizeStart}
        data-testid="registry-resize-handle"
      />

      {/* Header */}
      <div className="flex items-center justify-between border-b p-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Registries</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          title="Close sidecar"
          aria-label="Close registry sidecar"
          data-testid="registry-sidecar-close"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Search input */}
      <div className="border-b p-3 dark:border-gray-700">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search registries..."
            className="block w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-8 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-400"
            data-testid="registry-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
              data-testid="registry-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Registry tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {isRegistriesLoading ? (
          <div className="flex items-center justify-center py-8" data-testid="registry-loading">
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading registries...</div>
          </div>
        ) : registriesError ? (
          <div
            className="flex items-center justify-center py-8 text-sm text-red-600 dark:text-red-400"
            data-testid="registry-error"
          >
            Failed to load registries
          </div>
        ) : (
          <RegistryTree
            groups={groups}
            selectedRegistryId={selectedRegistryId}
            onRegistrySelect={onRegistrySelect}
            invoiceCounts={invoiceCounts}
            totalCount={isTotalLoading ? undefined : totalCount}
            isCountsLoading={isCountsLoading}
            searchQuery={searchQuery}
            selectedDocumentType={selectedDocumentType}
            onDocumentTypeSelect={onDocumentTypeSelect}
            documentTypeCounts={documentTypeCounts}
          />
        )}
      </div>

      {/* Footer with count summary */}
      {!isRegistriesLoading && !registriesError && registries && (
        <div className="border-t p-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {registries.length} {registries.length === 1 ? "registry" : "registries"}
          {searchQuery && filteredRegistries && filteredRegistries.length !== registries.length && (
            <span> ({filteredRegistries.length} matching)</span>
          )}
        </div>
      )}
    </aside>
  );
}
